import { LinkCellRenderer } from "@/controls/ag-grid/components/ButtonCellRenderer";
import { ChipCellRenderer } from "@/controls/ag-grid/components/ChipCellRenderer";
import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import { Grid } from "@material-ui/core";
import { GridReadyEvent } from "ag-grid-community";

import {
	CellClassParams,
	ColDef,
	GridApi,
	ValueFormatterParams,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useGridDefinitions } from "../../../../controls/ag-grid/components/GridDefinitions";
import { GenericReportHeader } from "../../models/GenericReportHeader";
import { useGenericReportStyles } from "../../styles/gridStyles";
import { useGenericReportContext } from "../GenericReportContext";

type Props = {
	headers: GenericReportHeader[];
	refreshData: () => void;
	rows: any[];
	gridRef: any;
	loading: boolean;
};

const moment = getMomentTz();

export const ReportTable = ({
	headers,
	rows,
	gridRef,
	refreshData,
	loading,
}: Props) => {
	const classes = useGenericReportStyles();
	const { state } = useGenericReportContext();
	const isSlagReport = state.reportCode === "mes-lab-slag-samples";
	const isMechanicalByActivity =
		state.reportCode === "mes-lab-mechanical-activity";
	const isMechanicalReport = "mes-report-mechanical";

	const { columnGenericDefaults, columnTypes } = useGridDefinitions({
		OnEdit: (data: any) => {},
	});
	const [columns, setColumns] = useState<ColDef[]>([]);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const valueRichFormatter = (params: ValueFormatterParams) => {
		if (params.value === null || params.value === undefined) return "";
		const metaData = params.value.toString().split("%%%");
		if (metaData.length > 0) return metaData[0];
	};
	const cellClassFormatter = (
		params: CellClassParams,
		defaultClass: string,
	) => {
		let classes = defaultClass + " ag-cell-center";

		if (!params.value) {
			return classes;
		}

		const metaData = params.value.toString().split("%%%");
		if (metaData.length > 2) {
			const dynamicClass = metaData[2].trim();
			return `${classes} ${dynamicClass}`;
		}
		return classes;
	};
	const CommentTooltip = ({
		value,
		showTooltip,
		color,
	}: { value: string; showTooltip: boolean; color: string }) => {
		if (!showTooltip) return null;
		return <div style={{ backgroundColor: color }}>{value}</div>;
	};

	const getCellWidth = (className: string) => {
		const startWidth = className.indexOf("pixel-");
		if (startWidth === -1) return 0;
		let endWidth = className.indexOf(" ", startWidth);
		if (endWidth === -1) endWidth = className.length;
		const width = className.substring(startWidth + 6, endWidth);
		return parseInt(width);
	};

	const handleGridReady = async (params: GridReadyEvent) => {
		await refreshData();
		setGridApi(params.api);
		params.api.sizeColumnsToFit();
	};

	const gridStyle = useMemo(() => {
		return {
			width: "100%",
			padding: "0px",
			height: "75vh",
			border: "1px solid #ccc",
			borderRadius: "4px",
		};
	}, []);

	useEffect(() => {
		if (gridApi) {
			gridApi.sizeColumnsToFit();
		}
	}, [gridApi, columns]);

	useMemo(() => {
		const newColumns: ColDef[] = [];
		headers
			.slice()
			.sort((a, b) => a.Order - b.Order)
			.forEach((header) => {
				const column: ColDef = {
					field: header.ColumnData,
					headerName: header.ColumnName,
					headerClass: header.HeaderClass,
					minWidth: getCellWidth(header.HeaderClass),
					resizable: true,
					...(isSlagReport && {
						cellClassRules: {
							"cell-alert": (params) => params.value?.includes("cell-alert"),
							"cell-success": (params) =>
								params.value?.includes("cell-success"),
						},
					}),
					valueFormatter: (params) => {
						return valueRichFormatter(params);
					},
					valueGetter: (params) => {
						const column = get(params.data, header.ColumnData, "_");

						if (header.Type === 10 && moment.utc(column).isValid())
							return dxtToLocalServerTime(
								moment(column).toDate(),
								"MM/dd/yyyy",
							);
						if (header.Type === 11 && moment.utc(column).isValid())
							return dxtToLocalServerTime(
								moment.utc(column).toDate(),
								"HH:mm:ss",
							);
						if (header.Type === 12 && moment.utc(column).isValid())
							return dxtToLocalServerTime(
								moment.utc(column).toDate(),
								"HH:mm:ss - MM/dd/yyyy",
							);
						else return params.data[header.ColumnData];
					},
					cellClass: (params) => {
						return cellClassFormatter(params, header.CellClass);
					},
					tooltipComponent: CommentTooltip,
					tooltipField: header.ColumnData,
					tooltipComponentParams: { color: "#ececec" },
				};
				if (header.ColumnData === "StatusCode") {
					column.cellRenderer = (props: any) => {
						if (isMechanicalByActivity || isMechanicalReport) {
							const { value } = props;

							const getStyles = () => {
								const baseStyles = {
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: "30px",
									height: "30px",
									borderRadius: "4px",
									fontWeight: "bold",
									cursor: "pointer",
								};

								if (value === "V") {
									return {
										...baseStyles,
										backgroundColor: "#4caf50",
										color: "white",
									};
								}
								if (value === "I") {
									return {
										...baseStyles,
										backgroundColor: "#f44336",
										color: "white",
									};
								}
								return {
									...baseStyles,
									backgroundColor: "#9e9e9e",
									color: "white",
								};
							};

							return (
								<div
									style={getStyles()}
									title={value === "V" ? "Valid" : "Invalid"}
								>
									{value}
								</div>
							);
						} else {
							return props.value;
						}
					};
				}
				if (header.ColumnData === "Heat") {
					column.cellRenderer = (params: any) => {
						if (isMechanicalByActivity) {
							return LinkCellRenderer({
								...params,
								link: `/lab-mechanical-by-heat/${params.data.Heat}`,
							});
						} else {
							return params.value;
						}
					};
				}
				if (header.ColumnData === "Asset") {
					column.cellRenderer = (params: any) => {
						const backgroundColor =
							params.value === "EAF 1" ? "#dae8fc" : "#d5e8d4";
						return (
							<div
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<ChipCellRenderer
									value={params.value}
									backgroundColor={backgroundColor}
								/>
							</div>
						);
					};
					column.cellClass = (params) => {
						return params.data.Asset
							? `eaf-1${params.data.Asset[params.data.Asset.length - 1]}`
							: "";
					};
				}
				newColumns.push(column);
			});
		setColumns(newColumns);
	}, [headers, isSlagReport]);
	gridRef.current = gridApi;
	return (
		<Grid container justifyContent="flex-start" alignItems="stretch">
			<Grid item style={gridStyle} xs={12} md={12}>
				<AgGridReact
					onGridReady={handleGridReady}
					rowSelection="single"
					pagination={false}
					animateRows={true}
					singleClickEdit={true}
					tooltipShowDelay={0}
					tooltipHideDelay={2000}
					rowData={rows}
					loading={loading}
					columnDefs={columns}
					onGridSizeChanged={(params) => {
						params.api.sizeColumnsToFit();
					}}
				/>
			</Grid>
		</Grid>
	);
};
